import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Layers, Expand, ChevronLeft, ChevronRight, X } from "lucide-react";

const GAME_LABEL = { pokemon: "Pokémon", one_piece: "One Piece" };
const GAME_COLOR = { pokemon: "#F59E0B", one_piece: "#3B82F6" };

// Authenticated image loader (img tags can't send auth cookies for blob-only routes reliably)
function useAuthImage(path) {
  const [state, setState] = useState({ src: null, failed: false });
  useEffect(() => {
    let url; let active = true;
    setState({ src: null, failed: false });
    if (!path) return;
    api.get(`/media/${path}`, { responseType: "blob" })
      .then((res) => { if (!active) return; url = URL.createObjectURL(res.data); setState({ src: url, failed: false }); })
      .catch(() => active && setState({ src: null, failed: true }));
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return state;
}

export function CardPlaceholder({ card, className = "" }) {
  const color = GAME_COLOR[card?.game] || "#7C3AED";
  return (
    <div className={`relative flex flex-col justify-between overflow-hidden rounded-lg border border-[#2A3655] bg-[#0B1020] p-3 ${className}`}
         style={{ aspectRatio: "3 / 4" }} data-testid="card-placeholder">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="relative flex items-center gap-1.5">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-[#CBD5E1]">{GAME_LABEL[card?.game] || "TCG"}</span>
      </div>
      <div className="relative flex flex-1 items-center justify-center"><Layers className="h-9 w-9" style={{ color }} /></div>
      <div className="relative">
        <p className="line-clamp-2 text-xs font-semibold text-[#F8FAFC]">{card?.name || "Carta"}</p>
        <p className="text-[10px] text-[#94A3B8]">{card?.expansion_name || card?.expansion_code || "Espansione n/d"}</p>
      </div>
    </div>
  );
}

// Catalog thumbnail: renders the public CDN card image (card.image_url) with a
// graceful fallback to the placeholder icon. No auth needed (external CDN).
export function CatalogThumb({ card, className = "" }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [card?.image_url]);
  if (!card?.image_url || failed) return <CardPlaceholder card={card} className={className} />;
  const color = GAME_COLOR[card?.game] || "#7C3AED";
  // Some hosts (One Piece) block cross-origin image loads (CORP: same-site);
  // route those through our backend proxy so the browser can display them.
  const src = card.image_url.includes("onepiece-cardgame.com")
    ? `${API}/catalog/image-proxy?u=${encodeURIComponent(card.image_url)}`
    : card.image_url;
  return (
    <div className={`relative overflow-hidden rounded-lg border border-[#2A3655] bg-[#0B1020] ${className}`}
         style={{ aspectRatio: "3 / 4" }} data-testid="catalog-thumb">
      <img src={src} alt={card?.name || "Carta"} loading="lazy" onError={() => setFailed(true)}
           className="h-full w-full object-contain" />
      <span className="absolute left-1.5 top-1.5 inline-block h-2 w-2 rounded-full ring-2 ring-[#0B1020]" style={{ background: color }} />
    </div>
  );
}

// Single authenticated image (used for avatars / simple cases)
export function CardImage({ path, card, className = "" }) {
  const { src, failed } = useAuthImage(path);
  if (!path || failed) return <CardPlaceholder card={card} className={className} />;
  if (!src) return <div className={`rounded-lg bg-[#1C2640] animate-pulse ${className}`} style={{ aspectRatio: "3 / 4" }} />;
  return <img src={src} alt={card?.name || "Foto carta"} className={`rounded-lg object-cover ${className}`} style={{ aspectRatio: "3 / 4" }} />;
}

function Lightbox({ photos, card, index, setIndex, onClose }) {
  const active = photos[index];
  const { src } = useAuthImage(active);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setIndex((i) => (i + 1) % photos.length); if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + photos.length) % photos.length); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [photos.length, onClose, setIndex]);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4" onClick={onClose} data-testid="card-lightbox" role="dialog" aria-modal="true">
      <button onClick={onClose} data-testid="lightbox-close" aria-label="Chiudi" className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
      <div className="relative max-h-[85vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
        {src ? <img src={src} alt={card?.name || "Foto carta"} className="max-h-[85vh] w-auto rounded-xl object-contain" />
             : <div className="flex h-96 w-72 items-center justify-center rounded-xl bg-[#141B2D] text-[#94A3B8]">Caricamento…</div>}
        <div className="mt-3 text-center text-sm text-[#F8FAFC]">{card?.name}{card?.expansion_name ? ` · ${card.expansion_name}` : ""}</div>
        {photos.length > 1 && (
          <>
            <button onClick={() => setIndex((i) => (i - 1 + photos.length) % photos.length)} aria-label="Precedente" data-testid="lightbox-prev" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>
            <button onClick={() => setIndex((i) => (i + 1) % photos.length)} aria-label="Successiva" data-testid="lightbox-next" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>
            <div className="mt-2 flex justify-center gap-1.5">{photos.map((_, i) => <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === index ? "bg-[#2DD4BF]" : "bg-white/30"}`} />)}</div>
          </>
        )}
      </div>
    </div>
  );
}

// Card media with gallery + click-to-zoom lightbox.
export function CardMedia({ photos = [], card, className = "", testid = "card-media" }) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const first = photos[0];
  const { src, failed } = useAuthImage(first);
  const hasPhoto = first && !failed;

  const openBox = () => { setIndex(0); setOpen(true); };

  if (!photos.length) return <div className={className} data-testid={testid}><CardPlaceholder card={card} /></div>;

  return (
    <div className={`relative ${className}`} data-testid={testid}>
      <button type="button" onClick={openBox} aria-label="Ingrandisci carta" className="group block w-full">
        {hasPhoto ? (
          src ? <img src={src} alt={card?.name || "Foto carta"} className="w-full rounded-lg object-cover" style={{ aspectRatio: "3 / 4" }} />
              : <div className="w-full rounded-lg bg-[#1C2640] animate-pulse" style={{ aspectRatio: "3 / 4" }} />
        ) : <CardPlaceholder card={card} />}
        <span className="absolute right-1.5 top-1.5 rounded-md bg-black/55 p-1.5 text-white opacity-80 transition-opacity group-hover:opacity-100" data-testid="card-zoom"><Expand className="h-4 w-4" /></span>
        {photos.length > 1 && <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white">{photos.length} foto</span>}
      </button>
      {open && <Lightbox photos={photos.length ? photos : []} card={card} index={index} setIndex={setIndex} onClose={() => setOpen(false)} />}
    </div>
  );
}

export { GAME_LABEL };
